import { useCallback, useEffect, useRef } from "react";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import {
  buildDispatchPlanningLiveSyncFingerprint,
  buildDispatchPlanningRuntimeRevisionUrl,
  completeDispatchPlanningDeferredRevisionWait,
  isDispatchPlanningRuntimeRevisionKnown,
  normalizeDispatchPlanningRuntimeRevision,
  resolveDispatchPlanningDeferredRevisionProbe,
  resolveDispatchPlanningCompactProbeDecision,
  resolveDispatchPlanningLiveSyncDelay,
  startDispatchPlanningDeferredRevisionWait,
} from "../utils/dispatchPlanningLiveSync.js";
import { buildScopedResultsLatestApiUrl } from "../utils/dispatchDateUrls.js";
import {
  isPlanningStateChangeInScope,
  subscribePlanningStateChange,
} from "../utils/planningCrossViewSync.js";
import {
  cancelDeferredPlanningLiveSync,
  deferPlanningLiveSyncUntilIdle,
  subscribePlanningLiveSyncActivity,
} from "../utils/planningMutationActivity.js";

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function buildRuntimeRevisionEtag(runtimeRevision) {
  const safeRevision = normalizeDispatchPlanningRuntimeRevision(runtimeRevision)
    .replaceAll("\\", "\\\\")
    .replaceAll("\"", "\\\"");
  return safeRevision ? `"${safeRevision}"` : "";
}

export default function useDispatchPlanningLiveSync({
  currentResultsData = null,
  dispatchDate = "",
  dispatchPeriod = "day",
  enabled = true,
  invalidateResultsCache,
  revalidator,
}) {
  const pendingRevalidationRef = useRef("");
  const pendingRecoveryRef = useRef(false);
  const lastLocalOperationRef = useRef("");
  const pendingTriggerRef = useRef("");
  const deferredRevalidationKeyRef = useRef(Symbol("dispatch-deferred-revalidation"));
  const requestFreshProbeRef = useRef(null);
  const requestedRevisionRef = useRef("");
  const externallyReloadedRevisionRef = useRef("");
  const locallySynchronizedRevisionRef = useRef(
    normalizeDispatchPlanningRuntimeRevision(currentResultsData?.runtime_revision),
  );
  const deferredRevisionWaitRef = useRef(null);
  const revalidationWasRunningRef = useRef(false);
  const latestStateRef = useRef(null);
  latestStateRef.current = {
    currentResultsData,
    dispatchDate,
    dispatchPeriod,
    enabled,
    invalidateResultsCache,
    revalidator,
  };

  const localFingerprint = buildDispatchPlanningLiveSyncFingerprint(currentResultsData);

  const runRevalidation = useCallback((runtimeRevision = "", { explicit = false, trigger = "runtime_revision_probe" } = {}) => {
    const latestState = latestStateRef.current;
    if (!latestState?.enabled || latestState.dispatchDate !== dispatchDate || latestState.dispatchPeriod !== dispatchPeriod) {
      return;
    }

    if (!explicit && deferPlanningLiveSyncUntilIdle(deferredRevalidationKeyRef.current, () => {
      // The event may predate our installed result. Compare a fresh revision
      // instead of replaying the event's stale revision after the update.
      requestFreshProbeRef.current?.();
    })) return;

    const requestedRevision = normalizeDispatchPlanningRuntimeRevision(runtimeRevision);
    const loadedRevision = normalizeDispatchPlanningRuntimeRevision(
      latestState.currentResultsData?.runtime_revision,
    );
    if (requestedRevision && (
      isDispatchPlanningRuntimeRevisionKnown({
        loadedRevision,
        locallySynchronizedRevision: locallySynchronizedRevisionRef.current,
        requestedRevision,
      })
      || requestedRevision === externallyReloadedRevisionRef.current
    )) {
      if (pendingRevalidationRef.current === requestedRevision) {
        pendingRevalidationRef.current = "";
      }
      return;
    }

    if (latestState.revalidator?.state !== "idle") {
      pendingRecoveryRef.current ||= explicit;
      pendingTriggerRef.current = trigger;
      pendingRevalidationRef.current = requestedRevision || "__planning_event__";
      return;
    }
    if (requestedRevision && requestedRevisionRef.current === requestedRevision) {
      return;
    }

    pendingRevalidationRef.current = "";
    requestedRevisionRef.current = requestedRevision || "__planning_event__";
    // A single bounded Performance entry identifies the actual reload trigger.
    // Observers can join it to the preceding local click without retaining data.
    try {
      globalThis.performance?.clearMarks?.("grain-dispatch-revalidation");
      globalThis.performance?.mark?.("grain-dispatch-revalidation", { detail: {
        trigger, operation_event_id: lastLocalOperationRef.current,
        requested_revision_known: Boolean(requestedRevision && requestedRevision === loadedRevision),
      } });
    } catch { /* Diagnostics must never prevent synchronization. */ }
    latestState.invalidateResultsCache?.(dispatchDate);
    latestState.revalidator?.revalidate();
  }, [dispatchDate, dispatchPeriod]);

  useEffect(() => {
    deferredRevisionWaitRef.current = null;
    lastLocalOperationRef.current = "";
    pendingTriggerRef.current = "";
    pendingRecoveryRef.current = false;
    revalidationWasRunningRef.current = false;
    cancelDeferredPlanningLiveSync(deferredRevalidationKeyRef.current);
    locallySynchronizedRevisionRef.current = "";
    pendingRevalidationRef.current = "";
    requestedRevisionRef.current = "";
    externallyReloadedRevisionRef.current = "";
    return () => cancelDeferredPlanningLiveSync(deferredRevalidationKeyRef.current);
  }, [dispatchDate, dispatchPeriod, enabled]);

  useEffect(() => {
    const revalidatorState = revalidator?.state;
    if (revalidatorState !== "idle") {
      revalidationWasRunningRef.current = true;
      return;
    }
    if (!revalidationWasRunningRef.current) {
      return;
    }

    revalidationWasRunningRef.current = false;
    const loadedRevision = normalizeDispatchPlanningRuntimeRevision(
      currentResultsData?.runtime_revision,
    );
    if (loadedRevision) {
      locallySynchronizedRevisionRef.current = loadedRevision;
    }
    deferredRevisionWaitRef.current = completeDispatchPlanningDeferredRevisionWait(
      deferredRevisionWaitRef.current,
      loadedRevision,
    );
    if (
      requestedRevisionRef.current === "__planning_event__"
      || requestedRevisionRef.current === loadedRevision
    ) {
      requestedRevisionRef.current = "";
    } else {
      // Le chargement a échoué ou a retourné l'ancienne version. Une prochaine
      // sonde pourra réessayer, sans rejouer une mutation.
      requestedRevisionRef.current = "";
    }

    const pendingRevision = pendingRevalidationRef.current;
    const explicit = pendingRecoveryRef.current;
    pendingRecoveryRef.current = false;
    pendingRevalidationRef.current = "";
    if (
      pendingRevision
      && (explicit || !isDispatchPlanningRuntimeRevisionKnown({
        loadedRevision,
        locallySynchronizedRevision: locallySynchronizedRevisionRef.current,
        requestedRevision: pendingRevision,
      }))
    ) {
      runRevalidation(
        explicit || pendingRevision === "__planning_event__" ? "" : pendingRevision,
        { explicit, trigger: pendingTriggerRef.current || "pending_revalidation" },
      );
    }
  }, [currentResultsData?.runtime_revision, revalidator?.state, runRevalidation]);



  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    return subscribePlanningStateChange((detail) => {
      if (!isPlanningStateChangeInScope(detail, { dispatchDate, dispatchPeriod })) {
        return;
      }
      if (detail?.source === "dispatch" && detail?.fromBroadcast !== true) {
        lastLocalOperationRef.current = normalizeInlineText(detail?.performanceChainId);
      }
      if (detail?.revalidateCurrentView === true) {
        runRevalidation("", { explicit: true, trigger: "explicit_view_recovery" });
        return;
      }
      if (detail?.deferRevalidationUntilRuntimeRevision === true) {
        deferredRevisionWaitRef.current = startDispatchPlanningDeferredRevisionWait({
          expectedRevision: detail?.expectedRuntimeRevision,
          loadedRevision: latestStateRef.current?.currentResultsData?.runtime_revision,
        });
        return;
      }
      if (
        detail?.actionName === "runtime_revision_conflict_reload"
        && detail?.fromBroadcast !== true
      ) {
        externallyReloadedRevisionRef.current = normalizeDispatchPlanningRuntimeRevision(
          detail?.runtimeRevision,
        );
        return;
      }
      const isLocallySynchronizedDispatchMutation = detail?.source === "dispatch"
        && detail?.fromBroadcast !== true;
      const localRuntimeRevision = normalizeDispatchPlanningRuntimeRevision(
        detail?.runtimeRevision,
      );
      if (isLocallySynchronizedDispatchMutation && localRuntimeRevision) {
        locallySynchronizedRevisionRef.current = localRuntimeRevision;
        return;
      }
      if (deferredRevisionWaitRef.current) return;
      const isPortalMutation = detail?.source === "portail";
      const isRemoteDispatchMutation = detail?.source === "dispatch"
        && detail?.fromBroadcast === true;
      if (!isPortalMutation && !isRemoteDispatchMutation) {
        return;
      }

      runRevalidation(detail?.runtimeRevision, { trigger: isPortalMutation ? "portal_event" : "remote_dispatch_event" });
    });
  }, [dispatchDate, dispatchPeriod, enabled, runRevalidation]);

  useEffect(() => {
    const safeDispatchDate = normalizeInlineText(dispatchDate);
    const safeDispatchPeriod = normalizeInlineText(dispatchPeriod).toLowerCase() === "evening"
      ? "evening"
      : "day";
    if (
      !enabled
      || !safeDispatchDate
      || typeof window === "undefined"
      || typeof document === "undefined"
    ) {
      return undefined;
    }

    const revisionUrl = buildDispatchPlanningRuntimeRevisionUrl(
      safeDispatchDate,
      safeDispatchPeriod,
    );
    const legacyResultsUrl = buildScopedResultsLatestApiUrl(
      safeDispatchDate,
      safeDispatchPeriod,
    );
    let cancelled = false;
    let activeController = null;
    let timerId = null;
    let etag = "";
    let failureCount = 0;
    let legacyMode = false;
    const backgroundProbeKey = Symbol("dispatch-planning-runtime-revision");

    const clearTimer = () => {
      if (timerId !== null) {
        window.clearTimeout(timerId);
        timerId = null;
      }
    };

    const scheduleNextProbe = (delayMs) => {
      clearTimer();
      if (
        cancelled
        || document.visibilityState === "hidden"
      ) {
        return;
      }
      if (deferPlanningLiveSyncUntilIdle(backgroundProbeKey, () => {
        if (!cancelled && document.visibilityState !== "hidden") {
          scheduleNextProbe(0);
        }
      })) {
        return;
      }
      timerId = window.setTimeout(() => {
        timerId = null;
        void probePlanningRevision();
      }, Math.max(Number(delayMs) || 0, 0));
    };

    const probeLegacyResults = async (controller) => {
      const response = await fetchWithSupabaseAuth(
        `${legacyResultsUrl}${legacyResultsUrl.includes("?") ? "&" : "?"}_dispatch_planning_probe=${Date.now()}`,
        {
          headers: { Accept: "application/json" },
          signal: controller.signal,
        },
      );
      if (!response.ok) {
        throw new Error(`Legacy planning probe failed with HTTP ${response.status}.`);
      }

      const payload = await response.json();
      if (cancelled || controller.signal.aborted) {
        return;
      }
      const remoteFingerprint = buildDispatchPlanningLiveSyncFingerprint(payload);
      const latestResultsData = latestStateRef.current?.currentResultsData;
      const currentFingerprint = buildDispatchPlanningLiveSyncFingerprint(latestResultsData);
      if (
        currentFingerprint
        && remoteFingerprint
        && remoteFingerprint !== currentFingerprint
      ) {
        runRevalidation(normalizeDispatchPlanningRuntimeRevision(payload?.runtime_revision), { trigger: "legacy_results_probe" });
      }
    };

    const fetchPlanningRevision = (controller) => fetchWithSupabaseAuth(revisionUrl, {
      headers: {
        Accept: "application/json",
        ...(etag ? { "If-None-Match": etag } : {}),
      },
      signal: controller.signal,
    });

    const applyPlanningRevisionResponse = async (response, controller) => {
      if (response.status === 304) {
        return;
      }
      if (!response.ok) {
        throw new Error(`Planning revision probe failed with HTTP ${response.status}.`);
      }

      const payload = await response.json();
      if (cancelled || controller.signal.aborted) {
        return;
      }
      const remoteRevision = normalizeDispatchPlanningRuntimeRevision(
        payload?.runtime_revision,
      );
      if (!remoteRevision) {
        throw new Error("Planning revision probe returned no runtime revision.");
      }
      etag = normalizeInlineText(response.headers.get("ETag"))
        || buildRuntimeRevisionEtag(remoteRevision);
      const wasWaitingForConcurrentMutation = Boolean(
        deferredRevisionWaitRef.current,
      );
      const deferredDecision = resolveDispatchPlanningDeferredRevisionProbe(
        deferredRevisionWaitRef.current,
        remoteRevision,
      );
      if (
        wasWaitingForConcurrentMutation
        && deferredDecision.shouldRevalidate
        && !completeDispatchPlanningDeferredRevisionWait(
          deferredDecision.waitState,
          latestStateRef.current?.currentResultsData?.runtime_revision,
        )
      ) {
        deferredRevisionWaitRef.current = null;
        return;
      }
      deferredRevisionWaitRef.current = deferredDecision.waitState;
      if (deferredDecision.shouldRevalidate) {
        runRevalidation(remoteRevision);
      }
    };

    const probePlanningRevision = async () => {
      if (
        cancelled
        || activeController
        || document.visibilityState === "hidden"
      ) {
        return;
      }
      if (deferPlanningLiveSyncUntilIdle(backgroundProbeKey, () => {
        if (!cancelled && document.visibilityState !== "hidden") {
          scheduleNextProbe(0);
        }
      })) {
        return;
      }

      const controller = new AbortController();
      activeController = controller;
      try {
        // En mode legacy, chaque cycle de 15 s reteste d'abord la sonde
        // compacte afin de quitter le fallback dès que le déploiement est prêt.
        const response = await fetchPlanningRevision(controller);
        if (cancelled || controller.signal.aborted) {
          return;
        }
        const probeDecision = resolveDispatchPlanningCompactProbeDecision({
          compactStatus: response.status,
          legacy: legacyMode,
        });
        legacyMode = probeDecision.legacy;

        if (probeDecision.probeLegacyResults) {
          etag = "";
          await probeLegacyResults(controller);
          failureCount = 0;
          return;
        }
        if (legacyMode) {
          // Compatibilité de déploiement uniquement : toutes les autres erreurs
          // restent sur la sonde compacte avec backoff.
          etag = "";
          failureCount = 0;
          return;
        }

        await applyPlanningRevisionResponse(response, controller);
        failureCount = 0;
      } catch (error) {
        if (!cancelled && !controller.signal.aborted && !(error instanceof DOMException && error.name === "AbortError")) {
          failureCount += 1;
        }
      } finally {
        const ownsProbe = activeController === controller;
        if (ownsProbe) activeController = null;
        if (ownsProbe && !cancelled && document.visibilityState !== "hidden") {
          scheduleNextProbe(resolveDispatchPlanningLiveSyncDelay({
            failureCount,
            legacy: legacyMode,
          }));
        }
      }
    };

    const requestFreshProbe = () => {
      etag = "";
      scheduleNextProbe(0);
    };
    requestFreshProbeRef.current = requestFreshProbe;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        clearTimer();
        activeController?.abort();
        activeController = null;
        return;
      }
      requestFreshProbe();
    };

    const unsubscribePlanningLiveSyncActivity = subscribePlanningLiveSyncActivity(({ isActive }) => {
      if (!isActive || cancelled) {
        return;
      }
      clearTimer();
      activeController?.abort();
      activeController = null;
      etag = "";
      deferPlanningLiveSyncUntilIdle(backgroundProbeKey, () => {
        if (!cancelled && document.visibilityState !== "hidden") {
          scheduleNextProbe(0);
        }
      });
    });

    document.addEventListener("visibilitychange", handleVisibilityChange);
    scheduleNextProbe(0);

    return () => {
      cancelled = true;
      unsubscribePlanningLiveSyncActivity();
      if (requestFreshProbeRef.current === requestFreshProbe) requestFreshProbeRef.current = null;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTimer();
      activeController?.abort();
      activeController = null;
      cancelDeferredPlanningLiveSync(backgroundProbeKey);
    };
  }, [dispatchDate, dispatchPeriod, enabled, runRevalidation]);

  return {
    localFingerprint,
  };
}
